/**
 * ADORACARE-PATIENT-MASTER-B2 — the front-desk patient search
 * (`/dashboard/patients`). Phone-first: one box accepts a mobile
 * number, a UMR / patient id, or a name (the EHNOTE section-13 trio);
 * the server detects the mode and returns the B0 disambiguation cards.
 *
 * Result handling (B0 front-desk flow):
 *   zero  → "register new patient" CTA (prefilled with the query);
 *   one   → open the patient record directly;
 *   many  → disambiguation picker (name, gender/age, registration
 *           branch, UMR). A family-shared phone is presented as a
 *           first-class case with an informational caption — never an
 *           error.
 *
 * SECURITY: the backend is the boundary (flag 503 before auth; role +
 * org scope in the handler). No phone digits appear in result rows —
 * only the B0 disambiguation fields (Tier 0 record view is the only
 * surface that shows contact values).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  fetchPatientMasterList,
  fetchPatientMasterSearch,
  type PatientListItem,
  type PatientListResponse,
} from '@/dashboard/api';
import {
  clinicLabelOf,
  clinicOptionLabel,
  useClinicLens,
} from '@/dashboard/clinic-context';
import { useDashboardMeContext } from '@/dashboard/me-context';
import PatientQuickView from '@/dashboard/patients/PatientQuickView';
import { EmptyState } from '@/dashboard/ui';
import {
  genderAgeLabel,
  patientCreateHref,
  deskRecordHref,
  sharedPhoneCaption,
  type PatientSearchResponse,
} from '@/dashboard/patients/patient-master-view';
import { ARRIVAL_SEARCH_UNAVAILABLE } from '@/dashboard/patients/register-wizard-model';

const SEARCH_HINT =
  'Search by mobile number, UMR (e.g. AC-000123), or patient name.';

export interface PatientSearchContentProps {
  readonly query: string;
  readonly onQueryChange?: (q: string) => void;
  readonly onSubmit?: () => void;
  readonly loading: boolean;
  readonly error: string | null;
  /** Null until the first search submits. */
  readonly result: PatientSearchResponse | null;
}

/**
 * Pure presentational body (no fetch) — rendered directly in tests via
 * react-dom/server (wrapped in a Router for links).
 */
export function PatientSearchContent({
  query,
  onQueryChange,
  onSubmit,
  loading,
  error,
  result,
}: PatientSearchContentProps): JSX.Element {
  return (
    <section aria-labelledby="patient-search-heading">
      <h1 id="patient-search-heading">Patients</h1>
      <p className="text-sm text-muted-foreground">{SEARCH_HINT}</p>

      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <input
          type="search"
          name="patient-search"
          aria-label="Search patients by mobile, UMR, or name"
          placeholder="Mobile / UMR / name"
          value={query}
          autoComplete="off"
          className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
          onChange={(e) => onQueryChange?.(e.target.value)}
        />
        <button
          type="submit"
          disabled={loading || query.trim().length < 2}
          className="rounded-md border border-border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error ? (
        // SEARCH FAILURE DEGRADED MODE (PR #414 corrective, case d): a
        // genuine search-service failure keeps registration available --
        // with the honest warning, through the search-failed origin door;
        // the server's duplicate-first check remains mandatory at submit.
        <div className="mt-3" role="alert">
          <p className="text-sm text-destructive">{error}</p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="patient-search-degraded">
            {ARRIVAL_SEARCH_UNAVAILABLE}
          </p>
          <Link
            to={patientCreateHref(query, 'search-failed')}
            className="mt-2 inline-block rounded-md border border-border bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            data-testid="patient-search-register-degraded"
          >
            Register new patient
          </Link>
        </div>
      ) : null}

      {result !== null && !loading && !error ? (
        <PatientSearchResults result={result} query={query} />
      ) : null}
    </section>
  );
}

function PatientSearchResults({
  result,
  query,
}: {
  result: PatientSearchResponse;
  query: string;
}): JSX.Element {
  if (result.count === 0) {
    return (
      <div className="mt-4 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
        <p className="font-medium text-foreground">No patient found.</p>
        <p className="mt-1 text-muted-foreground">
          No registered patient matches this {result.searchMode === 'phone'
            ? 'mobile number'
            : result.searchMode === 'umr'
              ? 'UMR'
              : 'name'}
          . Register them in under a minute.
        </p>
        <Link
          to={patientCreateHref(query, 'no-match')}
          className="mt-2 inline-block rounded-md border border-border bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          Register new patient
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {result.searchMode === 'phone' && result.count > 1 ? (
        <p className="text-sm text-muted-foreground" role="note">
          {sharedPhoneCaption(result.count)}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {result.count} match{result.count === 1 ? '' : 'es'} found.
        </p>
      )}
      <table className="mt-2 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Name</th>
            <th className="py-2 pr-3 font-medium">Gender / age</th>
            <th className="py-2 pr-3 font-medium">Registered at</th>
            <th className="py-2 pr-3 font-medium">UMR</th>
            <th className="py-2 pr-3 font-medium" aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {result.candidates.map((c) => (
            <tr key={c.patientId} className="border-b border-border/60">
              <td className="py-2 pr-3 font-medium text-foreground">
                {c.fullName}
                {c.status === 'inactive' ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (inactive)
                  </span>
                ) : null}
              </td>
              <td className="py-2 pr-3">{genderAgeLabel(c.gender, c.ageYears)}</td>
              <td className="py-2 pr-3">
                {c.registeredClinicId !== null
                  ? clinicLabelOf(c.registeredClinicLabel)
                  : '—'}
              </td>
              <td className="py-2 pr-3 font-mono text-xs">{c.umr}</td>
              <td className="py-2 pr-3">
                <Link
                  to={deskRecordHref(c.patientId, 'search')}
                  className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Open
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-sm">
        Not the right person?{' '}
        <Link
          to={patientCreateHref(query, 'none-of-these')}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Register a new patient
        </Link>
        {result.searchMode === 'phone'
          ? ' on the same number — shared family phones are supported.'
          : '.'}
      </p>
    </div>
  );
}

/** STITCH-2 SCOPE 10 (F10): the this-clinic, recent-first patient LIST. */
export function PatientListSection({
  list,
  loading,
  error,
  clinicLabel,
  onOpen,
}: {
  list: PatientListResponse | null;
  loading: boolean;
  error: string | null;
  clinicLabel: string | null;
  onOpen: (patient: PatientListItem) => void;
}): JSX.Element {
  return (
    <section className="clinic-table" aria-label="Patients at this clinic" data-testid="patient-list-section">
      <h2 className="clinic-table__title">
        {clinicLabel !== null ? `Patients - ${clinicLabel}` : 'Patients at this clinic'}
      </h2>
      {loading ? (
        <p className="loading" data-testid="patient-list-loading">
          Loading patients&hellip;
        </p>
      ) : error !== null ? (
        <p className="workboard__feedback--error" data-testid="patient-list-error">
          {error}
        </p>
      ) : list === null || list.patients.length === 0 ? (
        <EmptyState
          testId="patient-list-empty"
          title="No patients registered at this clinic yet."
          body="New registrations appear here the moment the front desk creates them. Use the search above to find a patient registered elsewhere."
        />
      ) : (
        <>
          <table className="table" data-testid="patient-list-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>UMR</th>
                <th>Last visit</th>
              </tr>
            </thead>
            <tbody>
              {list.patients.map((p) => (
                <tr key={p.patientId} data-testid="patient-list-row">
                  <td>
                    <button
                      type="button"
                      className="leads-row__name-btn"
                      data-testid="patient-list-open"
                      onClick={() => onOpen(p)}
                    >
                      {p.fullName}
                    </button>
                  </td>
                  <td>{p.umr}</td>
                  <td data-testid="patient-list-last-visit">
                    {p.lastVisitAt !== null
                      ? new Date(p.lastVisitAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          timeZone: 'Asia/Kolkata',
                        })
                      : 'No visits yet'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.capped ? (
            <p className="panel-caveat" data-testid="patient-list-capped">
              Showing the most recent {list.patients.length}. Search to find
              anyone else.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

export default function PatientSearchPage(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PatientSearchResponse | null>(null);

  // STITCH-2 SCOPE 10: the list-first block. Clinic = the global clinic
  // context selection when set, else the caller's first scoped clinic
  // (backend default). Search REFINES: submitting a search overlays results;
  // clearing it returns to the list.
  const meCtx = useDashboardMeContext();
  const clinicCtx = useClinicLens();
  const listClinicId =
    clinicCtx?.selectedClinicId ?? meCtx?.me?.clinicIds?.[0] ?? null;
  const listClinicLabel =
    clinicCtx?.options.find((o) => o.clinicId === listClinicId) !== undefined
      ? clinicOptionLabel(
          clinicCtx!.options.find((o) => o.clinicId === listClinicId)!,
        )
      : null;
  const [list, setList] = useState<PatientListResponse | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [openPatient, setOpenPatient] = useState<PatientListItem | null>(null);
  const patientMasterEnabled = meCtx?.me?.patientMasterEnabled === true;
  // RECEPTION-OS-BUILD-V1 (UAT-03 + Q-06=B): the front desk NEVER gets a
  // browsable patient list -- search requires input, results are
  // query-matched only. The SERVER refuses the list read for the role
  // (getPatientMasterList); this gate keeps the surface honest (no fetch,
  // no section, no dead error) instead of rendering a denied read.
  const browseListAllowed =
    patientMasterEnabled && meCtx?.me?.role !== 'receptionist';

  useEffect(() => {
    if (!browseListAllowed) {
      setListLoading(false);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    fetchPatientMasterList(listClinicId)
      .then((data) => {
        if (!cancelled) setList(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setList(null);
          setListError(
            e instanceof Error ? e.message : 'Could not load the patient list',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listClinicId, browseListAllowed]);

  const submit = (): void => {
    const q = query.trim();
    if (q.length < 2 || loading) return;
    setLoading(true);
    setError(null);
    fetchPatientMasterSearch(q)
      .then((res) => {
        setLoading(false);
        if (res.count === 1 && res.candidates[0]) {
          // Exactly one match → open the patient directly (B0 flow).
          navigate(deskRecordHref(res.candidates[0].patientId, 'search'));
          return;
        }
        setResult(res);
      })
      .catch((err: unknown) => {
        setLoading(false);
        setResult(null);
        setError(err instanceof Error ? err.message : 'Search failed');
      });
  };

  return (
    <>
      <PatientSearchContent
        query={query}
        onQueryChange={setQuery}
        onSubmit={submit}
        loading={loading}
        error={error}
        result={result}
      />
      {/* The list renders whenever no search result is overlaid (F10:
          list-first; search refines) -- and NEVER for the front desk
          (UAT-03 + Q-06=B: the desk's page is search-only). */}
      {result === null && browseListAllowed ? (
        <PatientListSection
          list={list}
          loading={listLoading}
          error={listError}
          clinicLabel={listClinicLabel}
          onOpen={setOpenPatient}
        />
      ) : null}
      {openPatient !== null ? (
        <PatientQuickView
          patient={openPatient}
          clinicLabel={listClinicLabel}
          onClose={() => setOpenPatient(null)}
        />
      ) : null}
    </>
  );
}
